import { api, ApiError } from '../../lib/api'
import { dataParamsQuery, readDataTable } from '../../lib/dataTable'
import { isEnglish } from '../../lib/i18n'

/** Activity log and notification history, read from the authenticated API. */

const ACTIVITIES = '/SystemState/GetActivities'

/**
 * THE ROW HAS NO FIELD NAMES.
 *
 * `ActivityDto` declares its properties as `_1 _2 _3 _4` — the DataTables positional-array
 * convention leaked into a C# DTO — so the wire looks like
 *
 * ```json
 * {"_1": 0, "_2": "<a href=\"…/UsersDetails?UserId=…\">rawamadmin</a>", "_3": "حذف حساب", "_4": "09-08-2026 11:30 AM"}
 * ```
 *
 * They are, in order: row number, username-as-an-anchor, activity name, formatted date. Named
 * here so nothing downstream ever sees an underscore. API_FINDINGS.md §65.
 */
const ROW_NUMBER = '_1'
const USER_ANCHOR = '_2'
const ACTIVITY = '_3'
const WHEN = '_4'

/**
 * The username out of the anchor the handler builds.
 *
 * `GenerateUserLink` wraps the name in `<a href="{DomainUrl}/UsersDetails?UserId={guid}">`, and
 * returns the bare name when `DomainUrl` is empty — so both shapes arrive. The markup is dropped
 * either way and the screen builds its own link; the href is useless anyway, since `/UsersDetails`
 * is not a route in this product (the real one is `/UserDetailsAdmin`).
 */
function textFromAnchor(value) {
  if (!value) return ''
  const match = /<a[^>]*>([\s\S]*?)<\/a>/i.exec(value)
  return (match ? match[1] : value).replace(/<[^>]*>/g, '').trim()
}

/** The guid inside that anchor — the only place a row's user id appears. */
function idFromAnchor(value) {
  const match = /UserId=([0-9a-f-]{36})/i.exec(String(value ?? ''))
  return match ? match[1] : null
}

/**
 * A sort/compare key for a date the API already formatted as `dd-MM-yyyy hh:mm tt`.
 *
 * Compared as text it would order by day of month (§64). Nothing here sorts on it — the API's
 * order is fixed — but the summary needs to know which row is the most recent, and that must not
 * depend on string luck.
 */
function dateKey(formatted) {
  const parts = /^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}) (AM|PM)$/i.exec(String(formatted ?? '').trim())
  if (!parts) return 0
  const [, day, month, year, hour, minute, meridiem] = parts
  const hour24 = (Number(hour) % 12) + (meridiem.toUpperCase() === 'PM' ? 12 : 0)
  return Date.UTC(Number(year), Number(month) - 1, Number(day), hour24, Number(minute))
}

/**
 * The activity log — 5,682 rows here and one more on every login, so always server-paged.
 *
 * Three things this endpoint does that it does not announce:
 *
 * - **`length` is mandatory.** `DataTablesQueryParams` clamps it to 0–100 and the handler reads a
 *   non-nullable int, so a missing length is `Take(0)` — an empty list, HTTP 200. It is also a
 *   HARD CEILING of 100: there is no way to ask for the whole table.
 * - **The order is the table's.** The handler reads the DataTables `order[…]` by the column's
 *   NAME and falls back to latest-first; `ACTIVITY_LOG_SORTABLE` is what it knows.
 * - **`_1` is not a row number.** `queryParams?.Start ?? 0 + index + 1` binds as
 *   `Start ?? (0 + index + 1)`, and `Start` is never null — so every row on page 1 says 0 and
 *   every row on page 2 says 25. It is ignored here and the table numbers its own rows.
 */
export const ACTIVITY_LOG_SORTABLE = ['username', 'activity', 'activityDate']

export async function listActivities({ page = 1, pageSize = 25, query = '', sort } = {}, signal) {
  const params = dataParamsQuery({ page, pageSize, search: query ?? '', sort, columns: ACTIVITY_LOG_SORTABLE })

  const dto = await api.post(ACTIVITIES, params, { signal })
  const { rows, total, totalUnfiltered } = readDataTable(dto)

  return {
    rows: rows.map((row, index) => ({
      /* The DTO carries no id — the handler selects one and drops it — so identity is the row's
         position in the page it came from. Stable for as long as the page is on screen, which is
         all `getRowId` needs. */
      id: `${(page - 1) * pageSize + index}`,
      username: textFromAnchor(row[USER_ANCHOR]),
      userId: idFromAnchor(row[USER_ANCHOR]),
      activity: row[ACTIVITY] ?? '',
      // Pre-formatted `dd-MM-yyyy hh:mm tt`; displayed as received, never re-parsed (W6).
      activityDate: row[WHEN] ?? '',
      when: dateKey(row[WHEN]),
      // Present, and deliberately unused — see the note above.
      serverRowNumber: row[ROW_NUMBER],
    })),
    /* `total` is what the pager divides — the count the SEARCH matched. `totalUnfiltered` is
       every activity ever recorded, which is what the summary counts. */
    total,
    totalUnfiltered,
  }
}

/**
 * THE NOTIFICATION HISTORY OF ONE RECORD — an invitation, a group email.
 *
 * `GET /api/admin/notifications-details/batches?objectId=&templateTypes=` — a JSON sibling
 * of the `NotificationsDetails` page route, guarded for `UsersManagers` because that is who
 * opens the modal that reads it.
 *
 * The server filters by object and type, so nothing is narrowed again here. Each batch
 * carries its messages: the batch's own state says the RUN finished, and only the message
 * says whether the address was actually reached.
 */
export async function listObjectBatches({ objectId, templateTypes = [], signal } = {}) {
  if (!objectId) return []

  const params = new URLSearchParams({ objectId: String(objectId) })
  if (templateTypes.length > 0) params.set('templateTypes', templateTypes.join(','))

  const dto = (await api.get(`/api/admin/notifications-details/batches?${params}`, { signal })) ?? []

  return dto.map((batch) => ({
    id: batch.Id,
    state: batch.State ?? '',
    templateType: batch.TemplateType,
    objectId: batch.ObjectId ?? '',
    scheduledStartDate: batch.ScheduledStartDate ?? '',
    startDate: batch.StartDate ?? '',
    endDate: batch.EndDate ?? '',
    recipients: batch.Recipients ?? '',
    messages: (batch.Messages ?? []).map((message) => ({
      id: message.Id,
      deliveryStatus: message.DeliveryStatus ?? '',
      tries: message.Tries ?? 0,
      lastTry: message.LastTry ?? '',
      channel: message.Channel ?? '',
      receiver: message.Receiver ?? '',
      error: message.Error ?? '',
    })),
  }))
}

const NOTIFICATIONS_LOG = '/api/admin/notifications-details/log'

export async function getNotificationsLogPage(signal) {
  const dto = await api.get(`${NOTIFICATIONS_LOG}/`, { signal })
  return (dto?.Templates ?? []).map((template) => ({
    type: template.Id,
    name: (isEnglish ? template.NameEnglish : template.NameArabic) || template.NameArabic || template.NameEnglish || String(template.Id),
  }))
}

/** What `FilterNotificationLogHandler` and `FilterNotificationLogMessagesHandler` can order by. */
export const BATCH_SORTABLE = ['typeName', 'state', 'scheduledStartDate', 'startDate', 'endDate']
export const MESSAGE_SORTABLE = ['status', 'lastTry']

function logPaging(page, pageSize, sort, sortable) {
  return dataParamsQuery({ page, pageSize, sort, columns: sortable })
}

export async function listBatches({ type = '', status = '', page = 1, pageSize = 25, sort } = {}, signal) {
  const body = logPaging(page, pageSize, sort, BATCH_SORTABLE)
  if (type !== '') body.set('Type', String(type))
  if (status !== '') body.set('Status', status)
  const dto = await api.post(`${NOTIFICATIONS_LOG}/filter`, body, { signal })
  const result = readDataTable(dto)
  return {
    ...result,
    rows: result.rows.map((batch) => ({
      id: batch.Id,
      templateType: batch.TemplateType,
      state: batch.State ?? '',
      scheduledStartDate: batch.ScheduledStartDate ?? '',
      startDate: batch.StartDate ?? '',
      endDate: batch.EndDate ?? '',
    })),
    stateCounts: (dto.StateCounts ?? []).map((item) => ({ state: item.State, count: item.Count })),
  }
}

export async function listBatchMessages(batchId, { page = 1, pageSize = 25, sort } = {}, signal) {
  if (!batchId) throw new ApiError('A notification batch must be selected')
  const body = logPaging(page, pageSize, sort, MESSAGE_SORTABLE)
  body.set('BatchId', String(batchId))
  const result = readDataTable(await api.post(`${NOTIFICATIONS_LOG}/messages`, body, { signal }))
  return {
    ...result,
    rows: result.rows.map((message) => ({
      id: message.Id,
      status: message.Status ?? '',
      lastTry: message.LastTry ?? '',
      details: (message.Details ?? []).map((detail) => ({
        channel: detail.Channel ?? '',
        receiver: detail.Receiver ?? '',
        error: detail.Error ?? '',
        response: detail.Response ?? '',
        messageId: detail.MessageId ?? '',
      })),
    })),
  }
}

export async function resolveUncertainDelivery(notificationId, messageId, confirmedSent, traceReference) {
  const result = await api.post(`${NOTIFICATIONS_LOG}/resolve-delivery`, new URLSearchParams({
    NotificationId: String(notificationId), MessageId: messageId,
    ConfirmedSent: String(confirmedSent), TraceReference: traceReference.trim(),
  }))
  if (result?.Result !== true) throw new ApiError('Delivery review failed')
}

export async function deleteBatch(batchId) {
  try {
    const dto = await api.post(`${NOTIFICATIONS_LOG}/delete`, new URLSearchParams({ BatchId: String(batchId) }))
    return { ok: dto?.Result !== false, message: dto?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}
